/**
 * Générateur — Arrondir un nombre décimal à un rang demandé
 * Attendu ID : 606 | Niveau : 6ème | Domaine : NC
 *
 * Types générés : qcm_unique, texte (saisie), tableau_vf, relier, texte_trou_drag
 * Rangs au programme : unité, dixième, centième (millième HORS PROGRAMME).
 *
 * Mécanismes-clés testés (pool d'erreurs fiche cluster 5, section 4 ID 606) :
 *   - Règle du 5 (chiffre décisif ≥ 5 → arrondir au-dessus)
 *   - Décision < 5 (arrondi vers le bas)
 *   - Propagation de retenue (9,X → (X+1),0 enjambe la partie entière)
 *   - Troncature (piège classique)
 *   - Arrondi au mauvais rang (centième au lieu de dixième, etc.)
 *   - Oubli de propagation (9,975 → 9,0 au lieu de 10,0)
 *
 * Variantes estimées : >5000
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeClasser,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 606;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Les 3 rangs au programme 6ème. */
const RANGS = [
    { key: 'unite', libelle: "à l'unité", decimals: 0 },
    { key: 'dixieme', libelle: 'au dixième', decimals: 1 },
    { key: 'centieme', libelle: 'au centième', decimals: 2 },
];

// ─── Labels ───
const L_QCM = 'Identifier le bon arrondi parmi des distracteurs';
const L_SAISIE = "Donner l'arrondi à un rang demandé";
const L_VF = "Vrai/Faux sur des propositions d'arrondi";
const L_TROU = "Compléter un tableau d'arrondis";
const L_PROXIMITE_DROITE = "Lire la proximité d'un décimal sur une demi-droite graduée";

// ─── Helpers numériques ───

/**
 * Arrondi (demi-supérieur) fondé sur la CHAÎNE décimale, avec propagation de retenue.
 * Number.EPSILON (ancien roundTo) est trop petit pour combler l'écart IEEE 754 des
 * décimaux à mi-chemin : 8,995 (stocké 8,99499999…) donnait Math.round(899,499…)=899
 * → 8,99 au lieu de 9,00. On passe par toFixed(12) pour neutraliser l'erreur binaire,
 * puis on arrondit chiffre à chiffre avec retenue en cascade.
 */
const roundDecimalString = (s, decimals) => {
    s = String(s).replace(',', '.');
    let neg = false;
    if (s.startsWith('-')) { neg = true; s = s.slice(1); }
    let [ip, fr = ''] = s.split('.');
    fr = fr.padEnd(decimals + 1, '0');
    const keep = fr.slice(0, decimals);
    const nextDigit = fr.charCodeAt(decimals) - 48;
    const digits = (ip + keep).split('').map(d => +d);
    if (nextDigit >= 5) {
        let i = digits.length - 1;
        while (i >= 0) { digits[i]++; if (digits[i] < 10) break; digits[i] = 0; i--; }
        if (i < 0) digits.unshift(1);
    }
    const all = digits.join('');
    const intLen = all.length - decimals;
    const intResult = all.slice(0, intLen) || '0';
    const fracResult = all.slice(intLen);
    const numStr = decimals > 0 ? `${intResult}.${fracResult}` : intResult;
    return (neg ? -1 : 1) * Number(numStr);
};

/** Arrondi robuste : neutralise l'erreur IEEE 754 puis arrondit sur la chaîne (retenue). */
const roundTo = (n, decimals) => roundDecimalString(n.toFixed(12), decimals);

/** Troncature à n décimales (sans arrondir). */
const truncTo = (n, decimals) => {
    const factor = Math.pow(10, decimals);
    return Math.floor(n * factor + Number.EPSILON) / factor;
};

/** Formate un nombre en français avec EXACTEMENT n décimales. */
const toFrFixed = (n, decimals) => {
    if (decimals === 0) return String(Math.round(n + Number.EPSILON));
    return n.toFixed(decimals).replace('.', ',');
};

/** Format source avec n décimales précises (le nombre à arrondir). */
const toFrSource = (n, sourceDecimals) => n.toFixed(sourceDecimals).replace('.', ',');

/**
 * Génère un nombre source à 3 ou 4 décimales, non trivial au rang cible.
 * Respecte la règle d'exclusion fiche ligne 68 : le nombre n'est jamais déjà
 * juste à son rang cible.
 */
const randSource = (rangDecimals, intMin = 0, intMax = 20) => {
    for (let tries = 0; tries < 80; tries++) {
        const sourceDecimals = randomChoice([3, 4]);
        const factor = Math.pow(10, sourceDecimals);
        const intPart = randInt(intMin, intMax);
        const fracRaw = randInt(1, factor - 1);
        // Éviter trailing zero au dernier rang (nombre plus naturel)
        if (fracRaw % 10 === 0) continue;
        const n = intPart + fracRaw / factor;
        // Rejeter les sources dont le chiffre juste après le rang cible vaut exactement
        // 5 : cas à mi-chemin ambigu (convention scolaire = arrondi par excès), peu
        // propice à l'apprentissage et source d'erreurs de clé.
        const fracDigits = n.toFixed(sourceDecimals).split('.')[1] || '';
        if (fracDigits[rangDecimals] === '5') continue;
        const rounded = roundTo(n, rangDecimals);
        if (Math.abs(rounded - n) < 1e-12) continue; // déjà trivialement arrondi
        return { n, sourceDecimals };
    }
    return { n: 3.456, sourceDecimals: 3 };
};

/**
 * Génère un nombre source qui FORCE une propagation de retenue lors
 * de l'arrondi au rang cible (cas-clé fiche ligne 83).
 *   - Dixième : X,9Y où Y ≥ 5 (ex: 2,97 → 3,0 ; 9,975 → 10,0)
 *   - Centième : X,99Y où Y ≥ 5
 *   - Unité : intPart finit par 9 et dixième ≥ 5 (ex: 9,7 → 10)
 */
const randSourcePropagation = (rangDecimals) => {
    for (let tries = 0; tries < 40; tries++) {
        const sourceDecimals = randomChoice([3, 4]);
        let intPart;
        if (rangDecimals === 0) {
            intPart = randomChoice([9, 19, 29, 49]);
        } else {
            intPart = randInt(0, 19);
        }
        let fracStr = '';
        for (let k = 0; k < rangDecimals; k++) fracStr += '9';
        // randInt(6, 9) et non (5, 9) : on évite le chiffre décisif 5 (mi-chemin ambigu)
        // tout en garantissant la propagation de retenue.
        fracStr += String(randInt(6, 9));
        while (fracStr.length < sourceDecimals) fracStr += String(randInt(0, 9));
        // Éviter trailing zero
        if (fracStr.endsWith('0')) {
            fracStr = fracStr.slice(0, -1) + String(randInt(1, 9));
        }
        const fracRaw = parseInt(fracStr, 10);
        const n = intPart + fracRaw / Math.pow(10, sourceDecimals);
        const rounded = roundTo(n, rangDecimals);
        // Vérifier que propagation a bien lieu (partie entière change)
        if (Math.floor(rounded) !== Math.floor(n)) {
            return { n, sourceDecimals };
        }
    }
    return { n: 9.975, sourceDecimals: 3 };
};

// ═══════════════════════════════════════════════════════════
// 1. QCM UNIQUE — "Quel est l'arrondi au X de Y ?"
// ═══════════════════════════════════════════════════════════

const genArrondiQcm = () => {
    const rang = randomChoice(RANGS);
    const useProp = Math.random() < 0.35;
    const { n, sourceDecimals } = useProp
        ? randSourcePropagation(rang.decimals)
        : randSource(rang.decimals);
    const correct = roundTo(n, rang.decimals);
    const correctFr = toFrFixed(correct, rang.decimals);

    // Distracteurs — familles d'erreurs du pool fiche ligne 89-94
    const distractors = new Set();

    // (a) Troncature simple (pool ligne 89)
    const trunc = truncTo(n, rang.decimals);
    const truncFr = toFrFixed(trunc, rang.decimals);
    if (truncFr !== correctFr) distractors.add(truncFr);

    // (b) Arrondi au rang plus précis (pool ligne 91) — seulement si au programme
    if (rang.decimals + 1 <= 2 && rang.decimals + 1 < sourceDecimals) {
        const morePrecise = roundTo(n, rang.decimals + 1);
        const mpFr = toFrFixed(morePrecise, rang.decimals + 1);
        if (mpFr !== correctFr) distractors.add(mpFr);
    }

    // (c) Arrondi au rang moins précis (pool ligne 91 — autre sens)
    if (rang.decimals >= 1) {
        const lessPrecise = roundTo(n, rang.decimals - 1);
        const lpFr = toFrFixed(lessPrecise, rang.decimals - 1);
        if (lpFr !== correctFr) distractors.add(lpFr);
    }

    // (d) Oubli de propagation (pool ligne 94) — seulement si propagation a eu lieu
    if (Math.floor(correct) !== Math.floor(n)) {
        const oubli = toFrFixed(Math.floor(n), rang.decimals);
        if (oubli !== correctFr) distractors.add(oubli);
    }

    // Compléter à 3 distracteurs avec des voisins
    let safety = 0;
    const step = Math.pow(10, -rang.decimals);
    while (distractors.size < 3 && safety < 30) {
        const offset = randomChoice([-3, -2, -1, 1, 2, 3]);
        const val = Math.max(0, correct + offset * step);
        const vFr = toFrFixed(val, rang.decimals);
        if (vFr !== correctFr) distractors.add(vFr);
        safety++;
    }

    const distractorArr = [...distractors].slice(0, 3);
    const choix = [correctFr, ...distractorArr];

    return makeQcm(
        choix,
        correctFr,
        `<p>Quel est l'arrondi ${rang.libelle} de ${toFrSource(n, sourceDecimals)} ?</p>`,
        opts(L_QCM),
    );
};

// ═══════════════════════════════════════════════════════════
// 2. SAISIE — "Donne l'arrondi au X de Y."
// ═══════════════════════════════════════════════════════════

const genArrondiSaisie = () => {
    const rang = randomChoice(RANGS);
    const useProp = Math.random() < 0.25;
    const { n, sourceDecimals } = useProp
        ? randSourcePropagation(rang.decimals)
        : randSource(rang.decimals);
    const correct = roundTo(n, rang.decimals);
    const correctFr = toFrFixed(correct, rang.decimals);

    return makeSaisie(
        correctFr,
        `<p>Donne l'arrondi ${rang.libelle} de ${toFrSource(n, sourceDecimals)}.</p>`,
        { ...opts(L_SAISIE), strict: false },
    );
};

// ═══════════════════════════════════════════════════════════
// 3. VRAI/FAUX — 6 lignes équilibrées 3V/3F
// ═══════════════════════════════════════════════════════════

const genArrondiVF = () => {
    // 3 Vrai + 3 Faux, mix de rangs (unité, dixième, centième)
    const pattern = shuffle([0, 0, 0, 1, 1, 1]); // 0=Vrai, 1=Faux
    const rangsPool = shuffle([
        RANGS[1], RANGS[1], // 2× dixième
        RANGS[2], RANGS[2], // 2× centième
        RANGS[0],           // 1× unité
        RANGS[1],           // 1× dixième de plus (variété)
    ]);

    const lignes = [];
    const bonnesReponses = [];
    const seen = new Set();

    for (let i = 0; i < 6; i++) {
        const rang = rangsPool[i];
        let attempt = 0;
        let ligneAdded = false;
        while (!ligneAdded && attempt < 20) {
            // 30% propagation si rang le permet
            const useProp = Math.random() < 0.3;
            const { n, sourceDecimals } = useProp
                ? randSourcePropagation(rang.decimals)
                : randSource(rang.decimals);
            const correct = roundTo(n, rang.decimals);
            const correctFr = toFrFixed(correct, rang.decimals);

            let affichage;
            if (pattern[i] === 0) {
                // Vrai : afficher l'arrondi correct
                affichage = correctFr;
            } else {
                // Faux : choisir une erreur pédagogique typique
                const candidates = [];
                // Troncature
                const trunc = truncTo(n, rang.decimals);
                const truncFr = toFrFixed(trunc, rang.decimals);
                if (truncFr !== correctFr) candidates.push(truncFr);
                // ±1 step
                const step = Math.pow(10, -rang.decimals);
                const up = toFrFixed(correct + step, rang.decimals);
                const down = toFrFixed(Math.max(0, correct - step), rang.decimals);
                if (up !== correctFr) candidates.push(up);
                if (down !== correctFr) candidates.push(down);
                // Oubli propagation si pertinent
                if (Math.floor(correct) !== Math.floor(n)) {
                    const oubli = toFrFixed(Math.floor(n), rang.decimals);
                    if (oubli !== correctFr) candidates.push(oubli);
                }
                if (candidates.length === 0) { attempt++; continue; }
                affichage = randomChoice(candidates);
            }

            const sourceFr = toFrSource(n, sourceDecimals);
            const ligne = `L'arrondi ${rang.libelle} de ${sourceFr} est ${affichage}`;
            if (!seen.has(ligne)) {
                seen.add(ligne);
                lignes.push(ligne);
                bonnesReponses.push(pattern[i]);
                ligneAdded = true;
            }
            attempt++;
        }
        if (!ligneAdded) {
            // Fallback improbable : ligne canonique
            const fb = pattern[i] === 0
                ? `L'arrondi au dixième de 3,456 est 3,5`
                : `L'arrondi au dixième de 2,971 est 2,9`;
            if (!seen.has(fb)) {
                seen.add(fb);
                lignes.push(fb);
                bonnesReponses.push(pattern[i]);
            } else {
                // Dernier recours : ligne alternative
                const alt = pattern[i] === 0
                    ? `L'arrondi à l'unité de 7,512 est 8`
                    : `L'arrondi au centième de 2,3456 est 2,34`;
                lignes.push(alt);
                bonnesReponses.push(pattern[i]);
            }
        }
    }

    return makeVraiFaux(
        lignes,
        bonnesReponses,
        `<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
        opts(L_VF),
    );
};

// ═══════════════════════════════════════════════════════════
// 5. TEXTE À TROUS (DRAG) — 2 nombres × 2 rangs = 4 trous
// ═══════════════════════════════════════════════════════════

const genArrondiTrouDrag = () => {
    // Chaque source est arrondie AU DIXIÈME ET AU CENTIÈME : on écarte aussi les
    // sources dont le chiffre décisif au centième (millième) vaut 5 (mi-chemin).
    const midCent = (nData) =>
        ((nData.n.toFixed(nData.sourceDecimals).split('.')[1] || '')[2] === '5');

    // Nombre 1 : force propagation au dixième (cas-clé cœur)
    let n1Data, n1Dix, n1Cent;
    for (let t = 0; t < 20; t++) {
        n1Data = randSourcePropagation(1);
        n1Dix = toFrFixed(roundTo(n1Data.n, 1), 1);
        n1Cent = toFrFixed(roundTo(n1Data.n, 2), 2);
        if (n1Dix !== n1Cent && !midCent(n1Data)) break;
    }

    // Nombre 2 : cas standard au dixième (pas de propagation)
    let n2Data, n2Dix, n2Cent;
    for (let t = 0; t < 30; t++) {
        n2Data = randSource(1);
        n2Dix = toFrFixed(roundTo(n2Data.n, 1), 1);
        n2Cent = toFrFixed(roundTo(n2Data.n, 2), 2);
        const all = new Set([n1Dix, n1Cent, n2Dix, n2Cent]);
        if (all.size === 4 && !midCent(n2Data)) break;
    }

    const n1Src = toFrSource(n1Data.n, n1Data.sourceDecimals);
    const n2Src = toFrSource(n2Data.n, n2Data.sourceDecimals);

    const bonnes = [n1Dix, n1Cent, n2Dix, n2Cent];

    // Vérification finale
    if (new Set(bonnes).size < 4) {
        // Fallback canonique
        return makeTexteTrou(
            `L'arrondi au dixième de 9,975 est {{10,0}} et son arrondi au centième est {{9,98}}. L'arrondi au dixième de 2,364 est {{2,4}} et son arrondi au centième est {{2,36}}.`,
            `<p>Complète en faisant glisser les bonnes valeurs dans les cases.</p>`,
            { ...opts(L_TROU), mode: 'drag', distracteurs: ['9,9', '9,0', '2,3'] },
        );
    }

    const phrase = `L'arrondi au dixième de ${n1Src} est {{${n1Dix}}} et son arrondi au centième est {{${n1Cent}}}. L'arrondi au dixième de ${n2Src} est {{${n2Dix}}} et son arrondi au centième est {{${n2Cent}}}.`;

    // Distracteurs : 3 pièges de familles distinctes.
    // Garde NUMÉRIQUE (pas seulement textuelle) sur TOUS les candidats : une
    // troncature/valeur peut égaler en VALEUR un arrondi attendu sous une autre
    // écriture (« 10,00 » vs le trou « 10,0 », « 8,1 » vs « 8,10 ») —
    // l'étiquette serait indécidable en drag (A-TROU-NUM-EQ).
    const valNum = (s) => parseFloat(String(s).replace(',', '.'));
    const egaleUneBonne = (s) => bonnes.some((b) => Math.abs(valNum(b) - valNum(s)) < 1e-9);
    const distractors = new Set();
    // (a) Troncature n1 au dixième
    const n1DixTrunc = toFrFixed(truncTo(n1Data.n, 1), 1);
    if (!egaleUneBonne(n1DixTrunc)) distractors.add(n1DixTrunc);
    // (b) Oubli propagation (si applicable) — donne partie entière de n1 formatée au dixième
    if (Math.floor(roundTo(n1Data.n, 1)) !== Math.floor(n1Data.n)) {
        const oubli = toFrFixed(Math.floor(n1Data.n), 1);
        if (!egaleUneBonne(oubli)) distractors.add(oubli);
    }
    // (c) Troncature n2 au dixième
    const n2DixTrunc = toFrFixed(truncTo(n2Data.n, 1), 1);
    if (!egaleUneBonne(n2DixTrunc)) distractors.add(n2DixTrunc);
    // (d) Erreur-type « troncature au centième » de n2 (au lieu d'arrondir) : distracteur
    // ciblé qui remplace l'ancien voisin arbitraire « X,0 » (ex. 8,0) non motivé.
    const n2CentTrunc = toFrFixed(truncTo(n2Data.n, 2), 2);
    if (!egaleUneBonne(n2CentTrunc)) distractors.add(n2CentTrunc);
    // Fallback : voisins IMMÉDIATS (±0,1) de n2Dix — on n'utilise plus ±0,2 qui produisait
    // des valeurs « rondes » éloignées et non motivées (ex. 8,0 pour un arrondi 8,2).
    let safety = 0;
    while (distractors.size < 3 && safety < 20) {
        const offset = randomChoice([-1, 1]) * 0.1;
        const val = Math.max(0, roundTo(n2Data.n + offset, 1));
        const vFr = toFrFixed(val, 1);
        if (!egaleUneBonne(vFr)) distractors.add(vFr);
        safety++;
    }

    const distArr = [...distractors].slice(0, 3);

    return makeTexteTrou(
        phrase,
        `<p>Complète en faisant glisser les bonnes valeurs dans les cases.</p>`,
        { ...opts(L_TROU), mode: 'drag', distracteurs: distArr },
    );
};

// ═══════════════════════════════════════════════════════════
// 5. PROXIMITÉ SUR DROITE GRADUÉE — Template contextualisé
// ═══════════════════════════════════════════════════════════
//
// Inspiré du manuel : on donne une demi-droite graduée avec 2 dixièmes
// consécutifs repérés (ex: 4,6 et 4,7). Plusieurs nombres sont déjà placés
// dessus. Pour chaque nombre, l'élève coche visuellement de quel dixième
// il est le plus proche — c'est l'essence cognitive de l'arrondi au dixième.
//
// Format : Vrai/Faux avec colonnes custom ["4,6", "4,7"] (pas "Vrai"/"Faux").

/**
 * Helper central : génère 2 dixièmes consécutifs, la droite graduée avec
 * repères, et 6 valeurs équilibrées (4 centièmes + 2 millièmes).
 * Partagé par les 3 formats : V/F, Classer, QCM Multi.
 * @returns {null|Object} null si génération échoue (très improbable)
 */
const genProximiteDroiteBase = () => {
    // 2 dixièmes consécutifs avec marge de 0,05 de chaque côté
    // → span de 0,2 total → 20 graduations au centième (confort visuel)
    const baseInt = randInt(1, 8);
    const dix1 = randInt(0, 8);
    const d1 = roundTo(baseInt + dix1 / 10, 1);
    const d2 = roundTo(d1 + 0.1, 1);
    const lineStart = roundTo(d1 - 0.05, 2);
    const lineEnd = roundTo(d2 + 0.05, 2);

    // Génération des 6 valeurs (mix centièmes + millièmes près du milieu)
    const used = new Set();
    const values = [];

    const tryAdd = (v, decimals) => {
        if (v <= lineStart + 1e-6 || v >= lineEnd - 1e-6) return false;
        const rounded = Math.round(v * 1000) / 1000;
        // Exclure d1, d2 eux-mêmes et ±0,05 (équidistants)
        if (Math.abs(rounded - d1) < 1e-6 || Math.abs(rounded - d2) < 1e-6) return false;
        if (Math.abs(rounded - (d1 + 0.05)) < 1e-6) return false;
        const key = rounded.toFixed(4);
        if (used.has(key)) return false;
        used.add(key);
        values.push({ value: rounded, decimals });
        return true;
    };

    const nearD1 = () => values.filter((v) => Math.abs(v.value - d1) < Math.abs(v.value - d2)).length;
    const nearD2 = () => values.filter((v) => Math.abs(v.value - d2) < Math.abs(v.value - d1)).length;

    // 2 centièmes clairement proches de d1 (offsets -0,03 à +0,04)
    let safety = 0;
    while (nearD1() < 2 && safety < 50) {
        const offset = randomChoice([-3, -2, -1, 1, 2, 3, 4]);
        tryAdd(roundTo(d1 + offset / 100, 2), 2);
        safety++;
    }

    // 2 centièmes clairement proches de d2 (offsets +0,06 à +0,13)
    safety = 0;
    while (nearD2() < 2 && safety < 50) {
        const offset = randomChoice([6, 7, 8, 9, 11, 12, 13]);
        tryAdd(roundTo(d1 + offset / 100, 2), 2);
        safety++;
    }

    // 2 millièmes "près du milieu" : un de chaque côté
    safety = 0;
    const milliemesD1 = [44, 46, 47, 48, 49];
    const milliemesD2 = [51, 52, 53, 54, 56];
    while (values.length < 6 && safety < 50) {
        const target = values.length % 2 === 0 ? milliemesD1 : milliemesD2;
        const offset = randomChoice(target);
        tryAdd(roundTo(d1 + offset / 1000, 3), 3);
        safety++;
    }

    // Fallback : compléter avec n'importe quoi si pas encore 6
    safety = 0;
    while (values.length < 6 && safety < 30) {
        const offset = randInt(-8, 18);
        tryAdd(roundTo(d1 + offset / 100, 2), 2);
        safety++;
    }

    if (values.length < 6) return null;

    const shuffled = shuffle(values);
    const nearestIdx = shuffled.map((v) =>
        Math.abs(v.value - d1) < Math.abs(v.value - d2) ? 0 : 1,
    );

    const d1Str = toFrFixed(d1, 1);
    const d2Str = toFrFixed(d2, 1);

    // Droite graduée : seules les valeurs au centième sont placées (labels verts).
    // Les millièmes restent dans le tableau/les propositions uniquement.
    const elAdd = {
        version: 1,
        elements: [{
            type: 'numberline',
            config: {
                start: lineStart,
                end: lineEnd,
                step: 0.01,
                lineType: 'half',
                showArrow: true,
                showStart: false,
                showEnd: false,
                reperes: [d1, d2],
                points: shuffled
                    .filter((v) => v.decimals <= 2)
                    .map((v) => ({
                        value: v.value,
                        label: toFrFixed(v.value, v.decimals),
                        showValue: false,
                        color: '#22c55e',
                    })),
            },
        }],
    };

    return { d1Str, d2Str, shuffled, nearestIdx, elAdd };
};

// ─── Format A : Tableau V/F avec colonnes custom [d1, d2] ───

const genArrondiProximiteDroite = () => {
    const base = genProximiteDroiteBase();
    if (!base) return genArrondiQcm();
    const { d1Str, d2Str, shuffled, nearestIdx, elAdd } = base;

    const lignes = shuffled.map((v) => toFrFixed(v.value, v.decimals));
    const texte = `<p>On considère la demi-droite graduée suivante :</p>{{element:0}}<p>Pour chaque nombre, indique s'il est plus près de <strong>${d1Str}</strong> ou de <strong>${d2Str}</strong>.</p>`;

    return makeVraiFaux(lignes, nearestIdx, texte, {
        ...opts(L_PROXIMITE_DROITE),
        colonnes: [d1Str, d2Str],
        elements_additionnels: elAdd,
    });
};

// ─── Format B : Classer (drag-and-drop dans 2 catégories) ───

const genArrondiProximiteClasser = () => {
    const base = genProximiteDroiteBase();
    if (!base) return genArrondiQcm();
    const { d1Str, d2Str, shuffled, nearestIdx, elAdd } = base;

    const itemsD1 = [];
    const itemsD2 = [];
    shuffled.forEach((v, i) => {
        const str = toFrFixed(v.value, v.decimals);
        if (nearestIdx[i] === 0) itemsD1.push(str);
        else itemsD2.push(str);
    });

    const categories = [
        { name: `Plus près de ${d1Str}`, items: itemsD1 },
        { name: `Plus près de ${d2Str}`, items: itemsD2 },
    ];

    const texte = `<p>On considère la demi-droite graduée suivante :</p>{{element:0}}<p>Classe chaque nombre selon qu'il est plus près de <strong>${d1Str}</strong> ou de <strong>${d2Str}</strong>.</p>`;

    return makeClasser(categories, texte, {
        ...opts(L_PROXIMITE_DROITE),
        elements_additionnels: elAdd,
    });
};

// ─── Format C : QCM Multi (sélectionner les affirmations correctes) ───

const genArrondiProximiteQcmMulti = () => {
    const base = genProximiteDroiteBase();
    if (!base) return genArrondiQcm();
    const { d1Str, d2Str, shuffled, nearestIdx, elAdd } = base;

    // 3 propositions vraies + 3 fausses (inversion du dixième pour les fausses)
    const flags = shuffle([true, true, true, false, false, false]);
    const propositions = [];
    const correctTexts = [];

    shuffled.forEach((v, i) => {
        const valStr = toFrFixed(v.value, v.decimals);
        const trueDix = nearestIdx[i] === 0 ? d1Str : d2Str;
        const falseDix = nearestIdx[i] === 0 ? d2Str : d1Str;
        const text = `${valStr} est plus près de ${flags[i] ? trueDix : falseDix}.`;
        propositions.push(text);
        if (flags[i]) correctTexts.push(text);
    });

    const texte = `<p>On considère la demi-droite graduée suivante :</p>{{element:0}}<p>Parmi ces affirmations, lesquelles sont correctes ?</p>`;

    return makeQcm(propositions, correctTexts, texte, {
        ...opts(L_PROXIMITE_DROITE),
        elements_additionnels: elAdd,
    }, true);
};

// ─── Registre ───
const GENERATORS = {
    arrondi_qcm_unique: genArrondiQcm,
    arrondi_texte: genArrondiSaisie,
    arrondi_tableau_vf: genArrondiVF,
    arrondi_texte_trou_drag: genArrondiTrouDrag,
    proximite_droite_tableau_vf: genArrondiProximiteDroite,
    proximite_droite_classer: genArrondiProximiteClasser,
    proximite_droite_qcm_multiple: genArrondiProximiteQcmMulti,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'arrondi', label: "Arrondir un décimal à un rang demandé", formatsList: buildFormats('arrondi') },
    { id: 'proximite_droite', label: "Proximité d'un décimal sur une demi-droite graduée", formatsList: buildFormats('proximite_droite') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
